import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { deleteIn, setIn } from "common/utils/objectUtils";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { formatMandatoryReferences } from "./docMandatoryReferenceUtils";
import { docEntityHandleHash } from "./entity/core/handleHash";
import { DocEntityTransactErrorNotification } from "./entity/docEntityTransactErrorNotification";
import { isVirtualRoofBlock } from "./roofSystemTools";

interface MandatoryReference {
  readonly entityType: DocEntityType;
  readonly entityName: string;
}

/**
 * Entities with a mandatory instance-scoped LEVEL reference whose chain
 * includes the given component instance. Clearing the chain would dangle
 * the underlying level id, so deletion must be blocked.
 */
export function docComponentInstanceMandatoryReferences(
  doc: Doc,
  componentInstanceId: string,
): readonly MandatoryReference[] {
  const refs: MandatoryReference[] = [];
  for (const id in doc.roofs) {
    const roof = doc.roofs[id];
    if (isVirtualRoofBlock(roof.blocks)) {
      const chain = roof.blocks.levelUsage.componentInstanceChain;
      if (chain?.includes(componentInstanceId)) {
        refs.push({ entityType: "ROOF", entityName: roof.name });
      }
    }
  }
  return refs;
}

/** First host level (not owned by a component), else first composite level. */
export function firstChainlessPlanContentSourceId(doc: Doc): string | undefined {
  for (const level of sortEntitiesBySortIndex(Object.values(doc.levels))) {
    const ownerComponentId = doc.componentMembers?.[docEntityHandleHash({ type: "LEVEL", id: level.id })];
    if (!ownerComponentId) {
      return level.id;
    }
  }
  const compositeLevels = sortEntitiesBySortIndex(Object.values(doc.compositeLevels ?? {}));
  return compositeLevels[0]?.id;
}

function isChainlessPlanContentSourceId(doc: Doc, contentSourceId: string): boolean {
  if (doc.compositeLevels?.[contentSourceId]) {
    return true;
  }
  if (!doc.levels[contentSourceId]) {
    return false;
  }
  return !doc.componentMembers?.[docEntityHandleHash({ type: "LEVEL", id: contentSourceId })];
}

/**
 * Strip optional instance-scoped LEVEL-reference members whose chain includes
 * the given component instance. Consolidates plan view stripping and adds
 * composite level / plan viewport coverage.
 */
export function docCleanupOptionalComponentInstanceRefs(doc: Doc, componentInstanceId: string): Doc {
  for (const viewId in doc.planViews) {
    const view = doc.planViews[viewId];
    const filtered = view.levelIds.filter((m) => !m.componentInstanceChain?.includes(componentInstanceId));
    if (filtered.length !== view.levelIds.length) {
      doc = setIn(doc, ["planViews", viewId, "levelIds"], filtered);
    }
  }
  for (const clId in doc.compositeLevels) {
    const cl = doc.compositeLevels[clId];
    const filtered = cl.members.filter((m) => !m.componentInstanceChain?.includes(componentInstanceId));
    if (filtered.length !== cl.members.length) {
      doc = setIn(doc, ["compositeLevels", clId, "members"], filtered);
    }
  }
  for (const viewportId in doc.viewports) {
    const viewport = doc.viewports[viewportId];
    if (viewport.viewType !== "PLAN_VIEW") {
      continue;
    }
    if (!viewport.contentSourceComponentInstanceChain?.includes(componentInstanceId)) {
      continue;
    }
    // Ephemeral instance levels for this chain are gone. Clear the chain and rebind definition-level
    // content sources to a host/composite level so plan viewports regenerate with real geometry.
    doc = deleteIn(doc, ["viewports", viewportId, "contentSourceComponentInstanceChain"]);
    const contentSourceId = typeof viewport.contentSourceId === "string" ? viewport.contentSourceId : undefined;
    if (contentSourceId && !isChainlessPlanContentSourceId(doc, contentSourceId)) {
      const fallbackContentSourceId = firstChainlessPlanContentSourceId(doc);
      if (fallbackContentSourceId) {
        doc = setIn(doc, ["viewports", viewportId, "contentSourceId"], fallbackContentSourceId);
      }
    }
  }
  return doc;
}

export type ComponentInstanceLevelEliminationReason = "DELETE" | "REPLICATION_TO_REMAPPING";

/**
 * When a component instance is deleted or switched from replication to remapping,
 * instance-scoped replicated levels are eliminated. Block mandatory references;
 * strip optional instance-scoped level members (plan views, composite levels).
 */
export function docGuardComponentInstanceLevelElimination(
  doc: Doc,
  componentInstanceId: string,
  reason: ComponentInstanceLevelEliminationReason,
): Doc {
  const refs = docComponentInstanceMandatoryReferences(doc, componentInstanceId);
  if (refs.length > 0) {
    const verb = reason === "DELETE" ? "delete Component Instance" : "switch Component Instance to Remapping";
    throw new DocEntityTransactErrorNotification(
      `Cannot ${verb}: it is still referenced by ${formatMandatoryReferences(refs)}.`,
      "COMPONENT_INSTANCE",
    );
  }
  return docCleanupOptionalComponentInstanceRefs(doc, componentInstanceId);
}
